import React from 'react';
import { Eye, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { hasTemplateFile, hasTailorHtml, findTemplateDoc } from '../../utils/hrTemplates';

/**
 * Sleek HR material card with View / Tailor / Download actions.
 * item: { title, name, description, icon }  (icon = lucide component)
 */
const HRMaterialCard = ({ item, documents = [], onTailor, onView }) => {
  const { darkMode } = useTheme();
  const Icon = item.icon;
  const available = hasTemplateFile(documents, item.title);
  const tailorable = hasTailorHtml(documents, item.title);
  const version = String(findTemplateDoc(documents, item.title)?.version || '').trim();

  const handleView = () => onView(item);

  const ghostBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const disabledBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border ${
    darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
  } cursor-not-allowed`;

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${
        darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
          {item.emoji ? (
            <span className="text-2xl leading-none">{item.emoji}</span>
          ) : (
            Icon && <Icon className="w-5 h-5" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {item.name || item.title}
            </h4>
            {version && (
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                  darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
                }`}
              >
                v{version}
              </span>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleView}
          disabled={!available}
          title={available ? 'View document' : 'File not available yet'}
          className={available ? ghostBtn : disabledBtn}
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
        <button
          type="button"
          onClick={() => onTailor(item)}
          disabled={!tailorable}
          title={tailorable ? 'Tailor to your company' : 'Tailoring not available yet'}
          className={
            tailorable
              ? 'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors'
              : disabledBtn
          }
        >
          <Sparkles className="w-3.5 h-3.5" />
          Tailor
        </button>
      </div>
    </div>
  );
};

export default HRMaterialCard;
